"use client";

import { useEffect, useRef, useState } from "react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function OurManifesto() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.3
  });
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animationFrameRef = useRef<number>();
  const [isPaused, setIsPaused] = useState(false);
  const progressRef = useRef(0);
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    const resizeCanvas = () => {
      const container = canvas.parentElement;
      if (!container) return;
      canvas.width = container.offsetWidth;
      canvas.height = container.offsetHeight;
    };
    resizeCanvas();
    window.addEventListener("resize", resizeCanvas);
    const animate = () => {
      if (!canvas.width || !canvas.height) return;
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      if (!isPaused) {
        progressRef.current += 0.008;
        if (progressRef.current > 1) progressRef.current = 0;
      }
      const progress = progressRef.current;
      const centerX = canvas.width / 2;
      const centerY = canvas.height / 2;
      ctx.save();
      ctx.translate(centerX, centerY);
      const codeNodes = 6;
      const codeRadius = Math.min(canvas.width, canvas.height) * 0.25;
      for (let i = 0; i < codeNodes; i++) {
        const angle = i / codeNodes * Math.PI * 2 + progress * Math.PI * 2;
        const x = Math.cos(angle) * codeRadius * (1 - progress * 0.3);
        const y = Math.sin(angle) * codeRadius * (1 - progress * 0.3);
        const size = 8 - progress * 4;
        ctx.fillStyle = `hsla(215, 95%, 58%, ${1 - progress * 0.5})`;
        ctx.fillRect(x - size / 2, y - size / 2, size, size);
        if (i < codeNodes - 1) {
          ctx.strokeStyle = `hsla(215, 95%, 58%, ${0.4 - progress * 0.2})`;
          ctx.lineWidth = 1;
          ctx.beginPath();
          ctx.moveTo(x, y);
          const nextAngle = (i + 1) / codeNodes * Math.PI * 2 + progress * Math.PI * 2;
          const nextX = Math.cos(nextAngle) * codeRadius * (1 - progress * 0.3);
          const nextY = Math.sin(nextAngle) * codeRadius * (1 - progress * 0.3);
          ctx.lineTo(nextX, nextY);
          ctx.stroke();
        }
      }
      const humanRadius = Math.min(canvas.width, canvas.height) * 0.2;
      const bodyY = -humanRadius * 0.3;
      ctx.fillStyle = `hsla(265, 80%, 55%, ${progress})`;
      ctx.beginPath();
      ctx.arc(0, -humanRadius * 0.7, humanRadius * 0.25, 0, Math.PI * 2);
      ctx.fill();
      ctx.fillStyle = `hsla(265, 80%, 55%, ${progress})`;
      ctx.beginPath();
      ctx.ellipse(0, bodyY, humanRadius * 0.3, humanRadius * 0.5, 0, 0, Math.PI * 2);
      ctx.fill();
      const armAngle = Math.sin(progress * Math.PI * 4) * 0.4;
      ctx.strokeStyle = `hsla(265, 80%, 55%, ${progress})`;
      ctx.lineWidth = humanRadius * 0.12;
      ctx.lineCap = "round";
      ctx.beginPath();
      ctx.moveTo(-humanRadius * 0.2, bodyY - humanRadius * 0.2);
      ctx.lineTo(-humanRadius * 0.2 + Math.cos(armAngle) * humanRadius * 0.4, bodyY + Math.sin(armAngle) * humanRadius * 0.4);
      ctx.stroke();
      ctx.beginPath();
      ctx.moveTo(humanRadius * 0.2, bodyY - humanRadius * 0.2);
      ctx.lineTo(humanRadius * 0.2 + Math.cos(-armAngle) * humanRadius * 0.4, bodyY + Math.sin(-armAngle) * humanRadius * 0.4);
      ctx.stroke();
      const legAngle = Math.sin(progress * Math.PI * 4 + Math.PI) * 0.3;
      ctx.beginPath();
      ctx.moveTo(-humanRadius * 0.1, bodyY + humanRadius * 0.4);
      ctx.lineTo(-humanRadius * 0.1 + Math.sin(legAngle) * humanRadius * 0.3, bodyY + humanRadius * 0.4 + Math.cos(legAngle) * humanRadius * 0.5);
      ctx.stroke();
      ctx.beginPath();
      ctx.moveTo(humanRadius * 0.1, bodyY + humanRadius * 0.4);
      ctx.lineTo(humanRadius * 0.1 + Math.sin(-legAngle) * humanRadius * 0.3, bodyY + humanRadius * 0.4 + Math.cos(-legAngle) * humanRadius * 0.5);
      ctx.stroke();
      ctx.restore();
      animationFrameRef.current = requestAnimationFrame(animate);
    };
    const startAnimation = () => {
      if (canvas.width > 0 && canvas.height > 0) {
        animate();
      } else {
        setTimeout(startAnimation, 50);
      }
    };
    startAnimation();
    return () => {
      window.removeEventListener("resize", resizeCanvas);
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [isPaused]);
  return <section ref={ref} className="bg-dark-background text-foreground-dark py-24 px-6 lg:px-12 transition-all duration-[var(--duration-slow)] ease-[var(--ease-smooth)]" style={{
    opacity: isVisible ? 1 : 0,
    transform: isVisible ? "translateY(0)" : "translateY(20px)"
  }}>
      <div className="mx-auto grid max-w-7xl grid-cols-1 items-center gap-12 lg:grid-cols-2">
        <div className="space-y-6">
          <h2 className="font-display text-4xl font-bold leading-tight text-foreground-dark lg:text-5xl">
            Our Manifesto
          </h2>
          <div className="space-y-4 text-lg leading-relaxed text-foreground-dark/90">
            <p>
              We believe in <span className="text-primary font-semibold">remixing the workflow</span>—transforming administrative friction into creative freedom.
            </p>
            <p>
              By fusing <span className="text-secondary font-semibold">cybersecurity precision</span> with the kinetic energy of <span className="text-accent font-semibold">hip-hop dance</span>, we architect AI-driven systems that let dance studios and nonprofits reclaim their rhythm.
            </p>
            <p>
              Our mission is simple: <span className="text-primary font-semibold">Eliminate the chaos. Amplify the creativity.</span> High-ROI efficiency with a cyber-urban edge.
            </p>
            <p className="text-foreground-dark/80 italic">
              This is where code meets movement. Where analytics dance with artistry.
            </p>
          </div>
        </div>

        <div className="relative flex min-h-[400px] items-center justify-center lg:min-h-[500px]" onMouseEnter={() => setIsPaused(true)} onMouseLeave={() => setIsPaused(false)} style={{
        transform: isVisible ? "translateX(0)" : "translateX(40px)",
        opacity: isVisible ? 1 : 0,
        transition: "all 400ms cubic-bezier(0.34, 1.56, 0.64, 1)"
      }}>
          <canvas ref={canvasRef} className="h-full w-full transition-all duration-[var(--duration-medium)]" style={{
          filter: isPaused ? "drop-shadow(0 0 12px hsla(215, 95%, 58%, 0.6))" : "none"
        }} />
        </div>
      </div>
    </section>;
}